"use client";

import { useState, useMemo, useCallback } from "react";
import {
  Activity,
  DollarSign,
  Gauge,
  Zap,
  Brain,
  Sparkles,
  Shield,
  ShieldCheck,
  Globe,
  Rocket,
  Target,
  Star,
  Heart,
  Flame,
  Lightbulb,
  Lock,
  Eye,
  MessageSquare,
  Cpu,
  Database,
  Cloud,
  Settings,
  Layers,
  Box,
  Puzzle,
  Wand2,
  Bot,
  Code,
  Terminal,
  FileText,
  Image,
  Mic,
  Video,
  Search,
  Filter,
  BarChart,
  TrendingUp,
  Clock,
  Scale,
  ScanText,
  Text,
  Monitor,
  CircleCheck,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/shadcn/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { Button } from "@/components/shadcn/button";
import { NoxusFormLabel } from "../Form/NoxusFormLabel";

const ICON_MAP: Record<string, LucideIcon> = {
  Activity,
  DollarSign,
  Gauge,
  Zap,
  Brain,
  Sparkles,
  Shield,
  ShieldCheck,
  Globe,
  Rocket,
  Target,
  Star,
  Heart,
  Flame,
  Lightbulb,
  Lock,
  Eye,
  MessageSquare,
  Cpu,
  Database,
  Cloud,
  Settings,
  Layers,
  Box,
  Puzzle,
  Wand: Wand2,
  Wand2,
  Bot,
  Code,
  Terminal,
  FileText,
  Image,
  Mic,
  Video,
  Search,
  Filter,
  BarChart,
  TrendingUp,
  Clock,
  Scale,
  ScanText,
  Text,
  Monitor,
  CircleCheck,
};

// Convert PascalCase to kebab-case for storage (e.g. "DollarSign" -> "dollar-sign")
function toKebabCase(str: string): string {
  return str
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .toLowerCase();
}

// Convert kebab-case or snake_case to PascalCase (e.g. "dollar-sign" -> "DollarSign")
function toPascalCase(str: string): string {
  return str
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}

function resolveIcon(name: string): LucideIcon | null {
  if (!name) return null;
  if (name in ICON_MAP) return ICON_MAP[name];
  const pascal = toPascalCase(name);
  if (pascal in ICON_MAP) return ICON_MAP[pascal];
  return null;
}

export function LucideIconPicker({
  value,
  onChange,
  label = "Icon",
  isLarge = false,
  side = "bottom",
  align = "start",
}: {
  value: string;
  onChange: (icon: string) => void;
  label?: string;
  isLarge?: boolean;
  side?: "right" | "left" | "bottom" | "top";
  align?: "start" | "end" | "center";
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const CurrentIcon = resolveIcon(value);

  const displayedIcons = useMemo(() => {
    if (!search) return Object.keys(ICON_MAP);
    const lower = search.toLowerCase();
    return Object.keys(ICON_MAP).filter((name) =>
      name.toLowerCase().includes(lower)
    );
  }, [search]);

  const handleSelect = useCallback(
    (iconName: string) => {
      onChange(toKebabCase(iconName));
      setOpen(false);
      setSearch("");
    },
    [onChange]
  );

  return (
    <>
      <NoxusFormLabel label={label} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid="icon-picker-trigger"
            variant="outline"
            size={isLarge ? "default" : "icon"}
            className={cn(
              "w-full justify-center items-center gap-2 font-normal hover:bg-transparent focus-visible:border-input focus-visible:ring-ring focus-visible:ring-[1px]",
              open && "border-input ring-1 ring-ring",
              !isLarge && "w-8"
            )}
          >
            {CurrentIcon ? (
              <>
                <CurrentIcon className="size-4" strokeWidth={1.33} />
                {isLarge && (
                  <span className="font-mono text-xs text-primary">
                    {value}
                  </span>
                )}
              </>
            ) : value && isLarge ? (
              <span className="font-mono text-xs text-primary">{value}</span>
            ) : (
              <>
                {isLarge ? (
                  <span className="text-muted-foreground">Select an icon</span>
                ) : (
                  <div className="self-center justify-center h-4 w-4 rounded-sm bg-border" />
                )}
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0" align={align} side={side}>
          {/* <div className="p-2 pb-2">
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div> */}
          <div className="grid grid-cols-8 gap-1 max-h-[240px] overflow-y-auto p-2">
            {displayedIcons.map((iconName) => {
              const Icon = ICON_MAP[iconName];
              const kebab = toKebabCase(iconName);
              return (
                <Button
                  key={iconName}
                  title={iconName}
                  variant="ghost"
                  size="icon"
                  onClick={() => handleSelect(iconName)}
                  className={cn(
                    "flex items-center justify-center p-2 rounded-md hover:bg-accent transition-colors cursor-pointer",
                    value === kebab && "bg-accent ring-1 ring-primary"
                  )}
                >
                  <Icon className="size-4" strokeWidth={1.33} />
                </Button>
              );
            })}
          </div>
          {displayedIcons.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">
              No icons found
            </p>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}

/** Render a lucide icon by its kebab-case name. Returns null if not found. */
export function LucideIconByName({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  const Icon = resolveIcon(name);
  if (!Icon) return null;
  return <Icon className={className} />;
}
